import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

/**
 * Generic reusable Material-UI table with pagination
 */
const GenericMUITable = ({ fields, data, rowKey = "id", emptyStateContent, pagination }) => {
  const hasPagination = pagination && pagination.totalRecords > 0;

  return (
    <Box>
      <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 3 }}>
        <Table>
          {/* Table Header */}
          <TableHead>
            <TableRow sx={{ bgcolor: "#4A4A4A" }}>
              {fields.map((field) => (
                <TableCell key={field.key || field.label} align={field.align || "center"} sx={{ color: "#fff" }}>
                  {field.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {data && data.length > 0 ? (
              data.map((item, index) => (
                <TableRow key={item[rowKey] || index} hover>
                  {fields.map((field) => (
                    <TableCell
                      key={`${item[rowKey] || index}-${field.key || field.label}`}
                      align={field.align || "center"}
                    >
                      {field.render ? field.render(item) : item[field.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={fields.length} align="center">
                  {emptyStateContent || (
                    <Typography variant="body2" color="text.secondary">
                      No data available.
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {hasPagination && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            flexWrap: "wrap",
            gap: 2,
            pt: 2,
            borderTop: "1px solid #eee",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {(pagination.currentPage - 1) * pagination.itemsPerPage + 1} to{" "}
            {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalRecords)} of{" "}
            {pagination.totalRecords} records
          </Typography>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="items-per-page-label">Items per page</InputLabel>
            <Select
              labelId="items-per-page-label"
              value={pagination.itemsPerPage}
              label="Items per page"
              onChange={pagination.onItemsPerPageChange}
              aria-label="Select items per page"
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>

          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={(event, page) => {
              pagination.onPageChange(page);
            }}
            color="primary"
            showFirstButton
            showLastButton
            sx={{
              "& .MuiPaginationItem-root": {
                "&.Mui-selected": {
                  backgroundColor: "#ED141F",
                  color: "white",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default GenericMUITable;
